import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../../web/components-v2/Theme/theme';
import { shadowSm } from '../../../../../web/styles/shadows';
import { CompactWidth } from '../../../AdminDashboardPageLayout/styles';

/**
 * Chrome for the settings hub, group detail and search results.
 *
 * The design system these come from documents itself as extracted verbatim from `THEME`, so
 * every value here maps to a token rather than a literal. The fully-rounded chip uses a large
 * radius because CSS clamps it to half the box, which keeps it a pill at any height.
 */
const PILL_RADIUS = 999;

/**
 * The query container every collapsing layout below answers to.
 *
 * A viewport query is the wrong instrument here: this tab renders inside a column that the admin
 * right pane halves when its drawer opens, without the viewport changing at all, so a `max-width`
 * media query never fires for the case that actually needs it. `view` establishes the container;
 * naming it keeps these queries from latching onto some other containment context an ancestor
 * introduces later.
 */
const CONTAINER_NAME = 'adminAccountSettings';

/**
 * Content widths, in px, that decide how many columns a layout can carry.
 *
 * A settings row is a label beside a control that cannot shrink. Below roughly 440px the two stop
 * sharing a line and the control wraps underneath — legible, but two columns of wrapped rows read
 * worse than one column of whole ones, so that is where the second column goes rather than at the
 * 340px floor where they would actually start colliding.
 *
 * A hub card is a preview row's label beside its value. The value is short but unshrinkable, and the
 * label is the half that gets eaten: below roughly 420px of card the label is ellipsised down to a
 * few words, which is the opposite of what a card exists to do. Slightly under the settings figure
 * because a card carries no control, only text.
 */
const CARD_MIN_WIDTH = 420;
const SETTINGS_COLUMN_MIN_WIDTH = 440;
/**
 * A read-only value sits in a card, not across the hub, so it has `CARD_MIN_WIDTH` less the card's
 * padding to share with its neighbour — 372px at the narrowest a card gets before it loses its own
 * neighbour. Two of these plus the gap come to 336, so the pair holds all the way down.
 */
const READ_ONLY_ITEM_MIN_WIDTH = 160;

/** Side padding on `groupPanelBody`, which the settings columns inside it do not get to use. */
const GROUP_PANEL_INSET = THEME.spacing.lg * 2;

/** Container width below which a layout can no longer fit both of its columns. */
const HUB_TWO_COLUMN_MIN = CARD_MIN_WIDTH * 2 + THEME.spacing.lg;
const SETTINGS_TWO_COLUMN_MIN = SETTINGS_COLUMN_MIN_WIDTH * 2 + THEME.spacing.xl + GROUP_PANEL_INSET;

const containerNarrowerThan = (width: number) => `@container ${CONTAINER_NAME} (max-width: ${width - 1}px)`;

/**
 * How much width the admin shell hands back to the content column as the window gets *smaller*: at
 * `CompactWidth` it halves the scroller's side padding.
 *
 * The nav's own expanded-to-compact step is not part of this. The account page passes `compactNav`,
 * and the shell reads `!isWideScreen || compactNav`, so on this page the nav is the compact width at
 * every window size and never gives anything back.
 *
 * The gain matters because the container is that content column, so it steps this much wider the
 * moment the window crosses below the breakpoint — and a threshold inside the resulting band is
 * crossed twice, once going down and once coming back up.
 */
const SHELL_COMPACT_GAIN = (THEME.spacing.xl - THEME.spacing.md) * 2;

const SHELL_COMPACT = `@media only screen and (max-width: ${CompactWidth}px)`;

/**
 * Collapse a two-track layout below `minWidth` of container, and keep it collapsed.
 *
 * Once the shell is compact the second track costs `SHELL_COMPACT_GAIN` more, which is the same as
 * deciding on the width the container would have had if the shell had not just widened it. That
 * keeps the track count a monotonic function of the window: it can only ever go down as the window
 * narrows, whatever the drawer beside it is doing.
 *
 * The cost is the band just below the shell's breakpoint where the second track would now fit and is
 * not offered — `SHELL_COMPACT_GAIN` of window, which is one padding step rather than anything a
 * reader would notice.
 *
 * This on its own is not what made the columns pop; the page's own floor was (see `contentStyles` on
 * `AdminAccountDetailsPage`). It guards the step that is left once that floor is gone.
 */
const collapsedBelow = <T extends object>(minWidth: number, collapsed: T) => ({
	[containerNarrowerThan(minWidth)]: collapsed,
	[SHELL_COMPACT]: {
		[containerNarrowerThan(minWidth + SHELL_COMPACT_GAIN)]: collapsed,
	},
});

/**
 * The card has already given up its neighbour by this width, so it is as wide as the container and
 * there is nothing left to take — the row itself has to change shape instead of truncating further.
 *
 * Not a hypothetical: the app drawer is drag-resizable, so a wide drawer on a laptop leaves the tab
 * a column narrower than a single comfortable card.
 *
 * Left on the raw container width rather than going through `collapsedBelow`: this changes a row's
 * shape rather than how many tracks are offered, and a card here is already the container's full
 * width, so stacking it early on the shell's account would waste the row it is trying to save. Only
 * a drawer past ~680px could put the container in the band where the shell's jump matters.
 */
const CARD_STACKED = containerNarrowerThan(CARD_MIN_WIDTH);

/**
 * Grid tracks with both bounds: at least `minWidth` each, and never more than `maxColumns` sharing
 * the row.
 *
 * `auto-fit` on its own answers only the lower bound, so on a wide monitor it would deal a third and
 * a fourth value across a grid the design holds at two. Flooring each track at an even share of the
 * container caps how many can fit; `minWidth` still drops tracks when the container is narrow.
 */
const cappedTracks = (minWidth: number, maxColumns: number, gap: number) =>
	`repeat(auto-fit, minmax(max(${minWidth}px, (100% - ${(maxColumns - 1) * gap}px) / ${maxColumns}), 1fr))`;

export const settingsHubStyles = StyleSheet.create({
	/**
	 * Separates whichever view is showing from the search bar above it, and matches that bar's
	 * own 20px inset so the cards line up with it. 20 is off the spacing scale but is what the
	 * sibling blocks in this tab use, and alignment beats the token here.
	 */
	view: {
		margin: `${THEME.spacing.lg}px 20px 0`,
		// The width the three views measure themselves against. `inline-size` applies layout and
		// style containment but not paint, so nothing inside is clipped; the hover cards and select
		// menus that would care about a new containing block all portal to the body anyway.
		containerType: 'inline-size',
		containerName: CONTAINER_NAME,
	},

	// ---- Hub ----
	/** Two cards where two fit, one where they do not. */
	groupGrid: {
		display: 'grid',
		// Fixed at two rather than auto-fit: on a wide monitor a third and a fourth card would
		// otherwise deal themselves across a hub the design holds at two.
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: THEME.spacing.lg,
		...collapsedBelow(HUB_TWO_COLUMN_MIN, { gridTemplateColumns: '1fr' }),
	},
	card: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowSm,
		padding: THEME.spacing.lg,
		width: '100%',
		boxSizing: 'border-box' as const,
		// Grid cells stretch to their row, and the two cards sharing a row rarely summarise to the
		// same height — three preview rows against `Permanent`'s two rows of values. A column lets
		// the link below take the slack rather than leaving it under the card.
		display: 'flex',
		flexDirection: 'column' as const,
	},
	cardHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		marginBottom: THEME.spacing.xs,
	},
	cardIcon: {
		color: THEME.colors.primary.main,
		flex: 'none',
	},
	cardTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		// The only item in the header that can give: its three siblings are all `flex: none`, so
		// without this the longest name ("Pre Approved Content") pushes the count off the card
		// instead of ellipsing itself.
		minWidth: 0,
		overflow: 'hidden' as const,
		textOverflow: 'ellipsis' as const,
		whiteSpace: 'nowrap' as const,
	},
	cardCount: {
		marginLeft: 'auto',
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		flex: 'none',
	},
	cardInfoIcon: {
		display: 'inline-flex',
		alignItems: 'center',
		color: THEME.colors.icon.gray,
		flex: 'none',
		cursor: 'help',
		':hover': {
			color: THEME.colors.primary.main,
		},
	},
	cardTooltip: {
		maxWidth: 260,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.4,
		color: THEME.colors.text.main,
	},
	previewList: {
		display: 'flex',
		flexDirection: 'column' as const,
	},
	previewRow: {
		display: 'flex',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px 0`,
		borderTop: `1px solid ${THEME.colors.border.light}`,
		fontSize: THEME.typography.fontSizes.xs,
		// Once the card is this narrow the value has taken all the room the label needed, so the pair
		// stops sharing a line. Two short lines beat one line of ellipsis: the point of a preview is
		// knowing which setting it is.
		[CARD_STACKED]: {
			flexDirection: 'column' as const,
			// Deliberately off-scale, and the same 2 `readOnlyItem` uses: this is the identical
			// label-over-value pair, where even xs opens a gap that reads as two separate rows.
			gap: 2,
		},
	},
	previewLabel: {
		color: THEME.colors.text.main,
		minWidth: 0,
		overflow: 'hidden' as const,
		textOverflow: 'ellipsis' as const,
		whiteSpace: 'nowrap' as const,
		// On its own line there is no control to clip against, so the name can run to a second one.
		[CARD_STACKED]: {
			overflow: 'visible' as const,
			textOverflow: 'clip' as const,
			whiteSpace: 'normal' as const,
		},
	},
	previewValue: {
		color: THEME.colors.text.light,
		flex: 'none',
	},
	previewValueOn: {
		color: THEME.colors.success.dark,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	cardLink: {
		// Sits at the foot of the card whatever height its row settles on, but never closer than a
		// step to the summary above; padding rather than margin, which `auto` has taken.
		marginTop: 'auto',
		paddingTop: THEME.spacing.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
	},

	// ---- Read-only (Permanent) card ----
	readOnlyBadge: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		color: THEME.colors.text.light,
		backgroundColor: THEME.colors.background.dark,
		borderRadius: THEME.common.borderRadius,
		padding: `2px ${THEME.spacing.sm}px`,
		flex: 'none',
	},
	/**
	 * Two values across at most, dropping to one only if the card is squeezed below a pair.
	 *
	 * Two rather than four because the card is now a half-width cell like every other: four across
	 * would need the full-width row this card no longer takes, and on the hub's own single-column
	 * step it would deal `Permanent`'s four values into one line while its neighbours keep three
	 * rows — the card would read as a strip rather than a card.
	 *
	 * Intrinsic sizing rather than the container query the other two grids take: these are short
	 * label/value pairs with no control to crowd them, so there is no width at which they read badly
	 * — they only need to stop overflowing, and shedding the track at the last moment does that more
	 * gently than a step keyed off the container would.
	 */
	readOnlyGrid: {
		display: 'grid',
		gridTemplateColumns: cappedTracks(READ_ONLY_ITEM_MIN_WIDTH, 2, THEME.spacing.md),
		gap: THEME.spacing.md,
	},
	readOnlyItem: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 2,
		minWidth: 0,
	},
	readOnlyLabel: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	readOnlyValue: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},

	// ---- Group detail ----
	/**
	 * Back out, or switch group, on one line. Wraps rather than scrolls: there are only two controls,
	 * so a narrow container can afford to stack them.
	 */
	groupToolbar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap' as const,
		gap: THEME.spacing.md,
		marginBottom: THEME.spacing.lg,
	},
	/**
	 * `Button` sizes are built for standalone controls, so height and side padding are released for
	 * the two places a Button stands in for inline text (the card link and the search clear).
	 */
	inlineLinkButton: {
		height: 'auto',
		paddingLeft: 0,
		paddingRight: 0,
		fontSize: THEME.typography.fontSizes.xs,
	},
	/**
	 * The trigger sizes to its content, and group names run from "AI" to "Pre-Approved Content", so a
	 * floor keeps the control from resizing under the cursor each time a group is chosen.
	 */
	groupSelectTrigger: {
		minWidth: 240,
		maxWidth: '100%',
		gap: THEME.spacing.sm,
	},
	groupPanel: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowSm,
		overflow: 'hidden' as const,
	},
	groupPanelHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	groupPanelTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	groupPanelBody: {
		padding: `0 ${THEME.spacing.lg}px`,
	},
	/**
	 * Two columns of settings, opted into rather than inherited. With the help text behind its icon a
	 * row is a single line, so a whole group reads as one screenful.
	 *
	 * Search results share the panel body and deliberately do not take this: their chunks are ordered
	 * by relevance across every group, which two columns would shuffle into a reading order the
	 * ranking did not intend.
	 */
	groupPanelBodyColumns: {
		columnCount: 2,
		columnGap: `${THEME.spacing.xl}px`,
		// The second column drops out on the container's width, not the viewport's, so it also goes
		// when the right pane opens beside a screen wide enough that no media query would fire.
		...collapsedBelow(SETTINGS_TWO_COLUMN_MIN, { columnCount: 1 }),
	},
	/**
	 * A parent and its dependents are one unit, so a chunk must not be split down the middle by a
	 * column break.
	 */
	groupPanelBodyItem: {
		breakInside: 'avoid',
	},

	// ---- Parent / dependent treatment ----
	/**
	 * A parent and its dependents read as one chunk, so the line that separates settings is drawn
	 * here rather than by the parent row — which would otherwise cut the parent off from the
	 * dependents it owns.
	 */
	dependencyGroup: {
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	/** The panel edge closes off the last chunk, so its own line would double up against it. */
	dependencyGroupLast: {
		borderBottom: 'none',
	},
	/**
	 * The dormant state: `background.main` is the page-behind-cards grey, so a panel whose parent is
	 * off sits back from the white panel around it, matching the dropdowns it has disabled.
	 */
	dependentPanel: {
		// The left inset marks the panel as nested. It used to spend xxl + lg — 64px — on that, which a
		// column beside an open right pane cannot afford, and the rows inside paid for it by wrapping.
		// The rail carries the nesting, so md is enough to read it; the rest of the chunk's chrome is
		// kept to xs so a parent with dependents costs little more height than a parent without.
		margin: `${THEME.spacing.xs}px 0 ${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		padding: `${THEME.spacing.xs}px 0`,
		backgroundColor: THEME.colors.background.main,
		// border.light is the divider token — at #e9ebef it is invisible against this panel's own
		// #f9f9f9 fill, which left the nesting cue to the inset alone. border.main is the token meant
		// for an element's own edge.
		borderLeft: `3px solid ${THEME.colors.border.main}`,
		borderRadius: `0 ${THEME.common.borderRadius}px ${THEME.common.borderRadius}px 0`,
	},
	/**
	 * Enabled dependents are ordinary editable rows, so they take the foreground white every other
	 * live control in the panel sits on, and the grey is left to mean dormant. Both states were grey
	 * before, which read as disabled whichever way the parent was set.
	 *
	 * Not a primary tint: the rows label themselves in #A7ABAD, and primary.light behind that is
	 * 1.9:1 — the enabled state is the one you actually read and edit, so it gets the most legible
	 * ground rather than the most decorated one. The rail carries the state instead, at full
	 * primary.main; primary.light is documented for disabled buttons and backgrounds, and as a 3px
	 * line on grey it made the on/off signal the thing you could not see.
	 */
	dependentPanelActive: {
		backgroundColor: THEME.colors.background.light,
		borderLeftColor: THEME.colors.primary.main,
	},
	/**
	 * Heads the panel in both states — naming the parent while it is on, saying how to unlock the rows
	 * while it is off.
	 *
	 * Coloured to be read rather than to decorate: text.light is 2.7:1 on the dormant fill, and the
	 * theme rules it out over a tint. Sentence case at standard weight, since uppercase with tracking
	 * outweighed the rows it heads, and the rows label themselves in a light grey — a bold near-black
	 * line above them reads as an error rather than a heading.
	 */
	dependentCaption: {
		padding: `0 ${THEME.spacing.sm}px ${THEME.spacing.xs}px`,
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.main,
	},
	dependentRows: {
		padding: `0 ${THEME.spacing.sm}px`,
	},
	/** text.light on background.dark was 2.4:1 at 11px, and the theme bars it over a tint outright. */
	dependentCountBadge: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		backgroundColor: THEME.colors.background.dark,
		borderRadius: THEME.common.borderRadius,
		padding: '2px 6px',
		flex: 'none',
		whiteSpace: 'nowrap' as const,
	},
	/** primary.main over primary.light is 3.9:1, short of the 4.5:1 this size needs; dark is 7.3:1. */
	dependentCountBadgeActive: {
		color: THEME.colors.primary.dark,
		backgroundColor: THEME.colors.primary.light,
	},

	// ---- Search results ----
	searchSummary: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.md,
		marginBottom: THEME.spacing.md,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	resultGroupHeader: {
		padding: `${THEME.spacing.sm}px ${THEME.spacing.lg}px ${THEME.spacing.xs}px`,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.08em',
		color: THEME.colors.text.light,
		textTransform: 'uppercase' as const,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	resultGroupHeaderFirst: {
		borderTop: 'none',
	},
	/** Marks a parent shown only to give a matched dependent its context, so the count reads true. */
	contextBadge: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		backgroundColor: THEME.colors.background.dark,
		borderRadius: THEME.common.borderRadius,
		padding: '2px 6px',
		flex: 'none',
		whiteSpace: 'nowrap' as const,
	},
	noMatchRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap' as const,
		marginTop: THEME.spacing.md,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	noMatchChip: {
		backgroundColor: THEME.colors.background.dark,
		borderRadius: PILL_RADIUS,
		padding: `3px ${THEME.spacing.sm}px`,
		fontSize: THEME.typography.fontSizes.xs,
	},
	emptyState: {
		padding: THEME.spacing.xl,
		textAlign: 'center' as const,
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
});
